<template>
  <v-dialog
    max-width="400px"
    :fullscreen="mobile"
    transition="dialog-top-transition"
    persistent
    :model-value="modelValue"
  >
    <v-card>
      <v-card-title class="bg-primary d-flex align-center">
        <span class="headline text-uppercase">{{
          $t('preSignupDialog.title')
        }}</span>
        <v-spacer></v-spacer>
        <v-btn icon variant="text" color="white" @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-form v-if="false" validate-on="blur" @submit.prevent="save">
        <v-window v-model="step" disabled>
          <v-window-item :value="1">
            <v-card-text class="text-primary pt-3">
              <v-text-field
                v-model.trim="form.email"
                :rules="emailRules"
                :error-messages="formErrors.email"
                :label="$t('preSignupDialog.emailLabel')"
                required
              ></v-text-field>
              <v-radio-group
                color="primary"
                v-model="form.profilType"
                :rules="typeRules"
                :error-messages="formErrors.type"
              >
                <v-radio
                  :label="$t('preSignupDialog.practitionerOption')"
                  value="practitioner"
                ></v-radio>
                <v-radio
                  :label="$t('preSignupDialog.patientOption')"
                  value="patient"
                ></v-radio>
              </v-radio-group>
            </v-card-text>
          </v-window-item>
          <v-window-item :value="2">
            <v-card-text class="text-primary pt-3">
              <v-select
                v-model="form.countryCode"
                :items="countries"
                :rules="requiredRules"
                :error-messages="formErrors.country"
                :label="$t('preSignupDialog.countryLabel')"
                required
              ></v-select>
              <v-text-field
                v-model.trim="form.postalCode"
                :rules="requiredRules"
                :error-messages="formErrors.postalCode"
                :label="$t('preSignupDialog.postalCodeLabel')"
                maxlength="10"
                required
              ></v-text-field>
              <v-autocomplete
                v-if="form.profilType === 'practitioner'"
                v-model="form.disciplines"
                :items="disciplines"
                :rules="disciplinesRules"
                :error-messages="formErrors.disciplines"
                multiple
                chips
                closable-chips
                auto-select-first
                :label="$t('preSignupDialog.disciplinesLabel')"
                required
              >
                <template #no-data>
                  <v-list-item>
                    <v-list-item-title class="d-flex flex-column align-center">
                      <div>{{ $t('preSignupDialog.disciplinesNotFound') }}</div>
                      <v-btn
                        variant="text"
                        small
                        color="secondary"
                        @click="showOtherDisciplineField = true"
                      >
                        {{ $t('preSignupDialog.addDiscipline') }}
                      </v-btn>
                    </v-list-item-title>
                  </v-list-item>
                </template>
              </v-autocomplete>
              <v-text-field
                v-if="showOtherDisciplineField"
                v-model.trim="form.otherDiscipline"
                :error-messages="formErrors.otherDiscipline"
                :label="$t('preSignupDialog.otherDisciplineLabel')"
                required
              ></v-text-field>
            </v-card-text>
          </v-window-item>
          <v-window-item :value="3">
            <v-card-text class="pt-3">
              <div class="text-subtitle-1">
                {{ $t('preSignupDialog.subscriptionDurationTitle') }}
              </div>
              <p>{{ $t('preSignupDialog.subscriptionDiscountNote') }}</p>
              <v-sheet rounded class="pa-2 mx-n2 mt-2" color="secondary">
                <p class="">
                  <span class="font-weight-bold">{{
                    $t('preSignupDialog.discount1')
                  }}</span
                  ><br />
                  {{ $t('preSignupDialog.discount2') }}
                </p>
              </v-sheet>
              <p class="mt-2">
                <b>{{ $t('preSignupDialog.riskFreeNote') }}</b>
              </p>

              <div class="d-flex justify-space-between align-center mt-4">
                <span class="text-caption">{{
                  $t('preSignupDialog.monthlyRateLabel')
                }}</span>
                <div class="text-h6">
                  <span class="text-disabled text-decoration-line-through"
                    >{{ 50 }} €</span
                  >
                  <span class="font-weight-bold ml-2 text-primary">{{
                    monthlyPriceText
                  }}</span>
                </div>
              </div>
              <div class="d-flex justify-space-between align-center">
                <span class="text-caption">{{
                  $t('preSignupDialog.monthsLabel')
                }}</span>
                <span class="text-h6 font-weight-bold text-primary">{{
                  monthsIncluded
                }}</span>
              </div>
              <v-slider
                v-model="form.monthsPreSubscribed"
                :max="40"
                :min="1"
                :error-messages="formErrors.monthsPreSubscribed"
                step="1"
                tick-size="4"
                color="primary"
                hide-details
              ></v-slider>
              <div class="d-flex justify-space-between align-center">
                <span class="text-caption">{{
                  $t('preSignupDialog.subscriptionPriceLabel')
                }}</span>
                <div class="text-h6">
                  <span class="text-disabled text-decoration-line-through">
                    {{ form.monthsPreSubscribed * 50 }} €
                  </span>
                  <span class="font-weight-bold ml-2 text-primary"
                    >{{ form.monthsPreSubscribed * monthlyPrice }} €</span
                  >
                </div>
              </div>
              <div>
                <v-radio-group v-model="form.numberOfPaymentMonths">
                  <v-radio
                    :label="$t('preSignupDialog.payInOneGo')"
                    :value="1"
                    density="compact"
                  ></v-radio>
                  <v-radio
                    :label="`${$t('preSignupDialog.payIn3Installments')} (${((form.monthsPreSubscribed * monthlyPrice) / 3).toFixed(2)} € par mois)`"
                    :value="3"
                    density="compact"
                    :disabled="form.monthsPreSubscribed < 3"
                  ></v-radio>
                  <v-radio
                    :label="`${$t('preSignupDialog.payIn4Installments')} (${((form.monthsPreSubscribed * monthlyPrice) / 4).toFixed(2)} € par mois)`"
                    :value="4"
                    density="compact"
                    :disabled="form.monthsPreSubscribed < 4"
                  ></v-radio>
                </v-radio-group>
              </div>
              <v-expand-transition>
                <div v-show="form.monthsPreSubscribed > 0">
                  <v-text-field
                    class="mt-2 align-center"
                    @update:focused="fieldFocus(stripeCard)"
                    :error-messages="stripeError"
                  >
                    <div class="d-flex w-100 align-center">
                      <div
                        id="card-element"
                        style="width: 100%"
                        class="align-center"
                      >
                        <!-- Elements will create input elements here -->
                      </div>
                    </div>
                  </v-text-field>
                  <v-row dense>
                    <v-col cols="6">
                      <v-text-field
                        placeholder="MM / AA"
                        @update:focused="fieldFocus(stripeCardExpiry)"
                        hide-details
                      >
                        <div class="d-flex w-100 align-center">
                          <div id="card-expiry" style="width: 100%">
                            <!-- Elements will create input elements here -->
                          </div>
                        </div>
                      </v-text-field>
                    </v-col>
                    <v-col cols="6">
                      <v-text-field
                        placeholder="CVC"
                        @update:focused="fieldFocus(stripeCardCvc)"
                        hide-details
                      >
                        <div class="d-flex w-100 align-center">
                          <div id="card-cvc" style="width: 100%">
                            <!-- Elements will create input elements here -->
                          </div>
                        </div>
                      </v-text-field>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <div class="text-caption">
                        <v-icon size="14px" class="mr-1">mdi-lock</v-icon>
                        <span class="font-weight-bold">{{
                          $t('preSignupDialog.securityNote')
                        }}</span>
                      </div>
                    </v-col>
                  </v-row>
                </div>
              </v-expand-transition>
            </v-card-text>
          </v-window-item>
          <v-window-item :value="4">
            <v-card-text
              class="text-primary pt-3 d-flex flex-column align-center"
            >
              <v-sheet
                rounded="circle"
                color="secondary"
                height="70"
                width="70"
                class="d-flex justify-center align-center"
              >
                <v-icon size="x-large">mdi-check</v-icon>
              </v-sheet>
              <div class="text-center mt-3 font-weight-medium">
                <p>{{ $t('preSignupDialog.registrationCompleteTitle') }}</p>
                <p>{{ $t('preSignupDialog.registrationCompleteMessage') }}</p>
              </div>
            </v-card-text>
          </v-window-item>
        </v-window>
        <v-card-text v-show="globalErrors.length">
          <div class="text-red" v-for="error in globalErrors" :key="error">
            {{ error }}
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            v-if="![1, 4].includes(step)"
            color="primary"
            text
            @click="back"
          >
            {{ $t('preSignupDialog.backButton') }}
          </v-btn>
          <v-btn
            v-if="step !== 4"
            color="secondary"
            text
            type="submit"
            :loading="submitting"
          >
            {{ $t('preSignupDialog.submitButton') }}
          </v-btn>
          <v-btn v-else color="primary" text @click="$emit('close')">{{
            $t('preSignupDialog.closeButton')
          }}</v-btn>
        </v-card-actions>
      </v-form>
      <v-card-text v-else>
        <div class="text-center mb-2">
          Les pré-inscriptions rouvriront bientôt. Merci de votre patience.
        </div>
        <v-img
          src="@/assets/practitioner-presentation/On y est presque.jpg"
          class="mx-auto rounded-lg"
        ></v-img>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useDisplay, useTheme } from 'vuetify'
import { useStripe } from '@/shared/payment'
import { useStorage } from '@vueuse/core'

const { mobile } = useDisplay()

defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
})

const form = ref({
  email: '',
  profilType: '',
  countryCode: 'FR',
  postalCode: '',
  disciplines: [],
  monthsPreSubscribed: 4,
  otherDiscipline: '',
  numberOfPaymentMonths: 1,
})

const step = ref(1)

const formErrors = ref({})
const globalErrors = ref([])
// const stripeError = ref('')
const submitting = ref(false)

const showOtherDisciplineField = ref(false)
const emailRules = [
  v => !!v || "L'e-mail est requis",
  v => /.+@.+/.test(v) || "L'e-mail doit être valide",
]
const typeRules = [v => !!v || 'Ce champ est requis']
const requiredRules = [v => step.value !== 2 || !!v || 'Ce champ est requis']
// disciplines are required only when type is professional
const disciplinesRules = [
  v =>
    step.value !== 2 ||
    !!form.value.otherDiscipline ||
    (!!v && v.length) ||
    'Ce champ est requis',
]

const countries = [
  { title: 'France', value: 'FR' },
  { title: 'Belgique', value: 'BE' },
  { title: 'Suisse', value: 'CH' },
  { title: 'Luxembourg', value: 'LU' },
  { title: 'Canada', value: 'CA' },
  { title: 'Autre', value: 'other' },
]
const disciplines = [
  {
    title: 'Acupuncteur',
    value: 'acupuncturist',
  },
  {
    title: 'Aqua fitness',
    value: 'water-fitness',
  },
  {
    title: 'Aquaforme',
    value: 'water-aerobics',
  },
  {
    title: 'Aromathérapeute',
    value: 'aromatherapist',
  },
  {
    title: 'Art-thérapeute',
    value: 'art-therapist',
  },
  {
    title: 'Astrologue',
    value: 'astrologer',
  },
  {
    title: 'Auriculothérapeute',
    value: 'auriculotherapist',
  },
  {
    title: 'Autres ➕',
    value: 'other',
  },
  {
    title: 'Biofeedback',
    value: 'biofeedback',
  },
  {
    title: 'Biorésonance',
    value: 'bioresonance',
  },
  {
    title: 'Body Attack',
    value: 'body-attack',
  },
  {
    title: 'Body Balance',
    value: 'body-balance',
  },
  {
    title: 'Chiropracteur',
    value: 'chiropractor',
  },
  {
    title: 'Clown thérapeute',
    value: 'clown-therapy',
  },
  {
    title: 'Coach de vie',
    value: 'life-coach',
  },
  {
    title: 'Coach en développement personnel',
    value: 'personal-development-coach',
  },
  {
    title: 'Coach sportif',
    value: 'sports-coach',
  },
  {
    title: 'Conseiller en fleurs de Bach',
    value: 'bach-flower-therapist',
  },
  {
    title: 'Conseiller en hygiène vitale',
    value: 'vital-hygiene-advisor',
  },
  {
    title: 'Constellation familiale',
    value: 'family-constellation',
  },
  {
    title: 'Danse',
    value: 'dance',
  },
  {
    title: 'Diététicien',
    value: 'dietician',
  },
  {
    title: 'Doula',
    value: 'doula',
  },
  {
    title: 'Énergéticien',
    value: 'energy-healer',
  },
  {
    title: 'Équithérapeute',
    value: 'equitherapy',
  },
  {
    title: 'Étiopathe',
    value: 'ethiopath',
  },
  {
    title: 'Fasciathérapie',
    value: 'fasciatherapy',
  },
  {
    title: 'Feng Shui',
    value: 'feng-shui',
  },
  {
    title: 'Fitness',
    value: 'fitness',
  },
  {
    title: 'Gemmothérapie',
    value: 'gemmotherapy',
  },
  {
    title: 'Gestalt-thérapeute',
    value: 'gestalt-therapy',
  },
  {
    title: 'Gynécologie',
    value: 'gynecology',
  },
  {
    title: 'Géobiologue',
    value: 'geobiologist',
  },
  {
    title: 'Haptonomie',
    value: 'haptonomy',
  },
  {
    title: 'Homéopathie',
    value: 'homeopathy',
  },
  {
    title: 'Hydrothérapeute',
    value: 'hydrotherapist',
  },
  {
    title: 'Hydrothérapeute du côlon',
    value: 'colon-hydrotherapist',
  },
  {
    title: 'Hypnothérapeute',
    value: 'hypnotherapist',
  },
  {
    title: 'Instructeur de méditation',
    value: 'meditation-instructor',
  },
  {
    title: 'Instructeur de pilates',
    value: 'pilates',
  },
  {
    title: 'Instructeur de yoga',
    value: 'yoga-instructor',
  },
  {
    title: 'Instructeur de yoga du rire',
    value: 'laughter-yoga-instructor',
  },
  {
    title: 'Iridologue',
    value: 'iridologist',
  },
  {
    title: 'Kinésiologue',
    value: 'kinesiologist',
  },
  {
    title: 'Lithothérapeute',
    value: 'lithotherapist',
  },
  {
    title: 'Logothérapeute',
    value: 'logotherapy',
  },
  {
    title: 'Magnétiseur',
    value: 'magnetotherapist',
  },
  {
    title: 'Massothérapeute',
    value: 'massage-therapist',
  },
  {
    title: 'Microkinésithérapeute',
    value: 'microkinesitherapist',
  },
  {
    title: 'Médecin',
    value: 'medical-doctor',
  },
  {
    title: 'Médecine intégrative',
    value: 'integrative-medicine',
  },
  {
    title: 'Méthode TRE',
    value: 'tre-method',
  },
  {
    title: 'Naturopathe',
    value: 'naturopath',
  },
  {
    title: 'Neuroscience',
    value: 'neuroscience',
  },
  {
    title: 'Nutritionniste',
    value: 'nutritionist',
  },
  {
    title: 'Ostéopathe',
    value: 'osteopath',
  },
  {
    title: 'Physiothérapeute',
    value: 'physiotherapist',
  },
  {
    title: 'Phytothérapeute',
    value: 'herbalist',
  },
  {
    title: 'Praticien en PNL (Programmation Neuro-Linguistique)',
    value: 'nlp-practitioner',
  },
  {
    title: 'Praticien en massage Amma',
    value: 'amma-massage-practitioner',
  },
  {
    title: 'Praticien en médecine ayurvédique',
    value: 'ayurvedic-practitioner',
  },
  {
    title: 'Praticien en médecine traditionnelle chinoise',
    value: 'traditional-chinese-medicine-practitioner',
  },
  {
    title: 'Praticien en reiki',
    value: 'reiki-practitioner',
  },
  {
    title: 'Praticien en shiatsu',
    value: 'shiatsu-practitioner',
  },
  {
    title: 'Praticien en sophrologie',
    value: 'sophrologist',
  },
  {
    title: 'Psychiatrie',
    value: 'psychiatry',
  },
  {
    title: 'Psychogénéalogie',
    value: 'psychogenealogy',
  },
  {
    title: 'Psychothérapeute',
    value: 'psychotherapist',
  },
  {
    title: 'Qi Gong',
    value: 'qi-gong',
  },
  {
    title: 'RPM',
    value: 'rpm',
  },
  {
    title: 'Running',
    value: 'running',
  },
  {
    title: 'Réflexologue',
    value: 'reflexologist',
  },
  {
    title: 'Sage-femme',
    value: 'midwife',
  },
  {
    title: 'Sauna',
    value: 'sauna',
  },
  {
    title: 'Spa',
    value: 'spa',
  },
  {
    title: 'Sylvotherapie',
    value: 'sylvotherapy',
  },
  {
    title: 'Tantra',
    value: 'tantra',
  },
  {
    title: 'Tarot & Oracle',
    value: 'tarot-oracle',
  },
  {
    title: 'Thérapeute EMDR',
    value: 'emdr',
  },
  {
    title: 'Thérapeute en EFT (Emotional Freedom Techniques)',
    value: 'eft-therapist',
  },
  {
    title: 'Thérapeute en respiration',
    value: 'breathwork',
  },
  {
    title: 'Thérapeute en thérapie cognitivo-comportementale',
    value: 'cognitive-behavioral-therapist',
  },
  {
    title: 'Thérapeute en thérapie cranio-sacrale',
    value: 'craniosacral-therapy',
  },
  {
    title: 'Thérapeute en ventouses',
    value: 'cupping-therapy',
  },
  {
    title: 'Thérapeute familiale',
    value: 'family-therapist',
  },
  {
    title: 'Thérapeute psycho-corporel',
    value: 'psycho-corporal-therapy',
  },
  {
    title: 'Thérapie de la renaissance',
    value: 'rebirth-therapy',
  },
  {
    title: 'Thérapie familiale systémique',
    value: 'systemic-family-therapy',
  },
  {
    title: 'Thérapie sexuelle',
    value: 'sex-therapy',
  },
  {
    title: 'Thérapie somato-émotionnelle',
    value: 'somato-emotional-therapy',
  },
  {
    title: 'Thérapie sonore',
    value: 'sound-therapy',
  },
  {
    title: 'XCore',
    value: 'xcore',
  },
  {
    title: 'Zoothérapie',
    value: 'zootherapy',
  },
]

watch(
  () => form.value.disciplines,
  value => {
    if (value.includes('other') || form.value.otherDiscipline !== '') {
      showOtherDisciplineField.value = true
    }
  },
)

const monthlyPrice = computed(() => {
  // if(form.value.monthsPreSubscribed >= 40) {
  //   return 'Bénéficiez d\'un accès à vie'
  // }
  if (form.value.monthsPreSubscribed > 12) {
    return 25
  }
  return 33
})

const monthlyPriceText = computed(() => {
  if (form.value.monthsPreSubscribed > 12) {
    return '25 €'
  }
  return '33 €'
})

const monthsIncluded = computed(() => {
  return form.value.monthsPreSubscribed
})

watch(
  () => form.value.monthsPreSubscribed,
  value => {
    if (value < 3) {
      form.value.numberOfPaymentMonths = 1
    } else if (value === 3 && form.value.numberOfPaymentMonths === 4) {
      form.value.numberOfPaymentMonths = 3
    }
  },
)

const save = async event => {
  const validation = await event
  if (!validation.valid) {
    return
  }
  const success = await submit()
  if (!success) {
    return
  }
  if (step.value === 1 && form.value.email && form.value.profilType) {
    setTimeout(() => {
      step.value++
    }, 10)
    if (form.value.profilType === 'practitioner') {
      showOtherDisciplineField.value = true
    }
  } else if (
    step.value === 2 &&
    form.value.countryCode &&
    form.value.postalCode
  ) {
    if (form.value.profilType === 'practitioner') {
      if (form.value.disciplines.length || form.value.otherDiscipline) {
        step.value++
      }
    } else {
      step.value = 4
    }
  } else if (step.value === 3) {
    step.value++
  }
}

const submit = async () => {
  globalErrors.value = []
  formErrors.value = {}
  submitting.value = true
  const submitForm = { ...form.value, referrerSlug: refSlug.value }
  if (form.value.countryCode === 'other') {
    submitForm.countryCode = null
  }
  let result
  try {
    result = await fetch(`${import.meta.env.VITE_API_URL}/pre-signup/`, {
      method: 'POST',
      body: JSON.stringify(submitForm),
      headers: {
        'Content-Type': 'application/json',
      },
    })
  } catch (e) {
    globalErrors.value.push(
      'Une erreur est survenue. Veuillez réessayer plus tard.',
    )
  } finally {
    submitting.value = false
  }
  if (!result.ok) {
    if (result.status === 422) {
      const error = await result.json()
      const violations = error.violations
      for (const violation of violations) {
        if (!Object.hasOwn(form.value, violation.propertyPath)) {
          globalErrors.value.push(
            `${violation.propertyPath}: ${violation.title}`,
          )
        }
        formErrors.value[violation.propertyPath] = violation.title
      }
    }
    return false
  }
  if (step.value === 3) {
    const result = await submitPayment()
    return !!result
  }
  return true
}

const {
  stripe,
  stripeError,
  stripeCard,
  stripeCardExpiry,
  stripeCardCvc,
  initStripe,
} = useStripe(useTheme())

const submitPayment = async () => {
  if (stripeError.value.length) {
    return
  }
  if (!stripeCard.value._complete) {
    globalErrors.value.push('Veuillez remplir les champs de la carte bancaire.')
    return
  }
  submitting.value = true
  const finalAmount = form.value.monthsPreSubscribed * 25 + '.00'

  await stripe.value
    .createPaymentMethod({
      type: 'card',
      card: stripeCard.value,
      billing_details: {
        email: form.value.email,
      },
    })
    .then(result => {
      if (result.error) {
        globalErrors.value.push(result.error.message)
        submitting.value = false
      } else {
        createSinglePayment({
          paymentMethodId: result.paymentMethod.id,
          finalAmount,
        })
      }
    })
    .catch(() => {
      submitting.value = false
    })
}

const refSlug = useStorage('ref-slug', null)
const createSinglePayment = ({ paymentMethodId, finalAmount }) => {
  let url = `${import.meta.env.VITE_API_URL}/payment/create-payment-intent`
  if (form.value.numberOfPaymentMonths > 1) {
    url = `${import.meta.env.VITE_API_URL}/payment/create-spread-payment`
  }

  fetch(url, {
    method: 'POST',
    body: JSON.stringify({
      paymentMethodId,
      finalAmount,
      monthsPreSubscribed: form.value.monthsPreSubscribed,
      email: form.value.email,
      referrerSlug: refSlug.value,
      numberOfPaymentMonths: form.value.numberOfPaymentMonths,
    }),
    headers: {
      'Content-Type': 'application/json',
    },
  })
    .then(result => {
      return result.json()
    })
    .then(data => {
      if (data.error) {
        globalErrors.value.push(data.error)
        return true
      } else if (data.status === 'succeeded') {
        step.value = 4
        return true
      }
      stripe.value
        .confirmCardPayment(data.clientSecret, {
          payment_method: {
            card: stripeCard.value,
            billing_details: {
              email: form.value.email,
            },
          },
        })
        .then(result => {
          submitting.value = false
          if (result.error) {
            globalErrors.value.push(result.error.message)
          } else {
            step.value = 4
          }
        })
    })
    .then(result => {
      if (result) {
        submitting.value = false
      }
    })
    .catch(() => {
      submitting.value = false
      globalErrors.value.push(
        'Une erreur est survenue. Veuillez réessayer plus tard.',
      )
    })
}

const fieldFocus = field => {
  setTimeout(() => {
    field.focus()
  }, 10)
}

watch(step, currentStep => {
  if (currentStep === 3) {
    initStripe()
  }
})

const back = () => {
  if ([1, 4].includes(step.value)) {
    return
  }
  step.value--
}
</script>

<style scoped lang="scss">
:deep(.v-input) {
  .v-label--clickable {
    opacity: 1 !important;
  }
}
</style>
